import { useTranslation } from 'react-i18next';
import { Card } from '../Card';
import { Heading } from '../Heading';
import {
  AutoComplete,
  Form,
  FormInstance,
  Input,
  message,
  Select,
  Spin,
  Checkbox,
} from 'antd';
import { THEME_VARIABLE } from '@/config';
import { useEffect, useRef, useState } from 'react';
import { useDeleteContact, useFetchContact, useFetchContacts } from '@/hooksApi';
import { DeleteIcon } from '../Icons';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchAreaByProvinceCode, useFetchListProvinces } from '@/hooksApi/useRegion';
import { RegionItem } from '@/types/region';
import { parsePhoneNumber } from '@/utils';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { PhoneNumberInput } from '../PhoneNumberInput';
import { ContactProperties } from '@/types';
import { NationalNumber } from 'libphonenumber-js';
import { Phone, MapPin, BookUser, Search, X } from 'lucide-react';

const { Option } = Select;
export interface ContactData {
  name: string;
  phoneNumberPrimary: NationalNumber | undefined;
  countryCodePrimary: string;
  address: string;
  province: string;
  region_uid: string;
}
interface CustomerDetailsPropsCard {
  form: FormInstance;
  selectedProvinceCode?: string;
  setSelectedProvinceCode: (provinceCode?: string) => void;
  handleUpdatePreview: (contact: ContactData) => void;
  disableContactSearch?: boolean;
}

const CustomerDetailsCard = ({
  form,
  selectedProvinceCode,
  setSelectedProvinceCode,
  handleUpdatePreview,
  disableContactSearch,
}: CustomerDetailsPropsCard) => {
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [saveAddressChecked, setSaveAddressChecked] = useState(false);
  const [addressBookOpen, setAddressBookOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const addressBookRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  // Use the hook to fetch contacts
  const { data: contacts, isLoading: contactsLoading } = useFetchContacts();

  // Fetch individual contact by UID
  const { data: contactDetails } = useFetchContact(selectedUid || '');

  const { data: provinceList, isLoading: provinceListLoading } = useFetchListProvinces();
  const { data: areaList, isLoading: areaListLoading } = useFetchAreaByProvinceCode(
    selectedProvinceCode!,
  );

  const nameOptions = contacts?.object?.items.map((contact) => ({
    value: contact.full_name,
    uid: contact.uid, // Store the uid for later use
  }));

  useEffect(() => {
    if (selectedUid && contactDetails) {
      const contact = contactDetails.object;

      setSelectedProvinceCode(contact.region?.province_code || '');

      // Check if contact data exists before setting the fields
      if (contact) {
        const phoneInfo = parsePhoneNumber(contact.phone);
        // sae for secondary phone

        const _contact: ContactData = {
          name: contact.full_name || '',
          phoneNumberPrimary: phoneInfo.phone, // Set phone number without country code
          countryCodePrimary: phoneInfo.countryCode, // Set country code
          address: contact.address_text || '',
          province: contact.region?.province || '',
          region_uid: contact.region?.uid || '',
        };

        form?.setFieldsValue(_contact);
        setTimeout(() => {
          handleUpdatePreview(_contact);
        }, 500);
        // Set selected province code to fetch area list for the province
      }
    }
  }, [selectedUid, contactDetails, form]);

  // Close address book on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (addressBookRef.current && !addressBookRef.current.contains(e.target as Node)) {
        setAddressBookOpen(false);
      }
    };
    if (addressBookOpen) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [addressBookOpen]);

  // Handle province change to fetch area list based on selected province code
  const handleProvinceChange = (provinceCode: string) => {
    form?.setFieldsValue({ province: provinceCode });
    form?.setFieldsValue({ region_uid: undefined });

    setSelectedProvinceCode(provinceCode);
  };

  // handleAreaChange to save area UID to Zustand store and set the Field Value in form
  const handleAreaChange = (value: string) => {
    form?.setFieldsValue({ region_uid: value });
  };

  const queryClient = useQueryClient();

  // Handle delete action
  const { mutate: deleteContact } = useDeleteContact(); // Use the selected UID for deletion
  const handleDeleteContact = (uid: any) => {
    deleteContact(uid, {
      onSuccess: () => {
        queryClient.refetchQueries(['contacts']);
        message?.success(t('contact_removed'));
      },
    });
  };

  // Handle name selection
  const handleNameSelect = (name: string) => {
    const selectedContact = nameOptions?.find((option) => option.value === name);

    if (selectedContact) {
      // Store the selected UID to fetch full contact details
      setSelectedUid(selectedContact.uid);
      const contactDetail = contacts?.object?.items.find(
        (contact) => contact.uid === selectedContact.uid,
      );

      if (contactDetail) {
        const phoneInfo = parsePhoneNumber(contactDetail.phone);

        form?.setFieldsValue({
          phoneNumberPrimary: phoneInfo.phone,
          countryCodePrimary: phoneInfo.countryCode,
        });
      }
    }
  };

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    setSaveAddressChecked(e.target.checked);
  };

  const filterOptions = (input: string, option: any) => {
    const optionChildren = option.children;
    return optionChildren.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  };

  return (
    <Card>
      <div className='flex items-center justify-between mb-5'>
        <Heading
          title={t('customer_details')}
          fontSize='text-xs'
          fontWeight='font-bold'
          color='text-sd-muted-foreground'
          extraClassName='uppercase tracking-wider'
        />
        {/* O-20/O-20b: Contact book — matches redesign with search */}
        {!disableContactSearch && <div className='relative' ref={addressBookRef}>
          <button
            type='button'
            onClick={() => { setAddressBookOpen(!addressBookOpen); setContactSearch(''); }}
            className='inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-solid border-sd-input bg-sd-card text-[11px] font-medium text-sd-muted-foreground cursor-pointer hover:bg-sd-muted/20 transition-colors'
          >
            <BookUser className='h-3.5 w-3.5' />
            {String(t('contact_book', { defaultValue: 'Contact book' }))}
          </button>
          {addressBookOpen && (
            <div className='absolute top-full end-0 mt-1 z-50 bg-white rounded-xl border border-sd-border shadow-lg w-[320px]'>
              {/* Header */}
              <div className='flex items-center justify-between px-4 pt-3 pb-2'>
                <span className='text-[13px] font-semibold text-sd-foreground'>
                  {String(t('select_contact', { defaultValue: 'Select Contact' }))}
                </span>
                <button type='button' onClick={() => setAddressBookOpen(false)} className='p-1 cursor-pointer bg-transparent border-0 text-sd-muted-foreground hover:text-sd-foreground'>
                  <X className='h-3.5 w-3.5' />
                </button>
              </div>
              {/* Search */}
              <div className='px-3 pb-2'>
                <div className='flex items-center gap-2 h-9 px-3 rounded-lg border border-solid border-sd-input bg-sd-card'>
                  <Search className='h-3.5 w-3.5 text-sd-muted-foreground shrink-0' />
                  <input
                    type='text'
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder={String(t('search_by_name_or_phone', { defaultValue: 'Search by name or phone' }))}
                    className='flex-1 border-0 bg-transparent text-[12px] text-sd-foreground placeholder:text-sd-muted-foreground focus:outline-none'
                  />
                </div>
              </div>
              {/* Contact list */}
              <div className='max-h-[260px] overflow-y-auto border-t border-sd-border'>
                {(() => {
                  const items = contacts?.object?.items ?? [];
                  const q = contactSearch.toLowerCase();
                  const filtered = q ? items.filter((c: any) =>
                    (c.full_name || '').toLowerCase().includes(q) ||
                    (c.phone || '').includes(q)
                  ) : items;
                  if (filtered.length === 0) {
                    return (
                      <div className='px-4 py-6 text-center text-[12px] text-sd-muted-foreground'>
                        {contactsLoading ? String(t('loading')) : String(t('no_results', { defaultValue: 'No contacts found' }))}
                      </div>
                    );
                  }
                  return filtered.map((contact: any) => (
                    <button
                      key={contact.uid}
                      type='button'
                      onClick={() => {
                        setSelectedUid(contact.uid);
                        setAddressBookOpen(false);
                        setContactSearch('');
                      }}
                      className='w-full text-start px-4 py-3.5 cursor-pointer hover:bg-sd-muted/20 transition-colors border-0 bg-transparent border-b border-solid border-sd-border last:border-b-0 flex items-start gap-3'
                    >
                      <div className='shrink-0 w-10 h-10 rounded-full bg-sd-muted/30 flex items-center justify-center mt-0.5'>
                        <BookUser className='h-4 w-4 text-sd-muted-foreground' />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <span className='text-[13px] font-semibold text-sd-foreground block'>{contact.full_name}</span>
                        <span className='text-[11px] text-sd-muted-foreground block mt-0.5'>{contact.phone}</span>
                        {contact.address_text && (
                          <span className='text-[10px] text-sd-muted-foreground/60 block mt-0.5 truncate'>{contact.address_text}</span>
                        )}
                      </div>
                    </button>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>}
      </div>
      {/* Contact Info section */}
      <div className='mb-4'>
        <div className='flex items-center gap-2 mb-3'>
          <Phone className='h-3.5 w-3.5 text-sd-muted-foreground' strokeWidth={1.5} />
          <span className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground'>
            {String(t('contact_info'))}
          </span>
        </div>
        {/* Name field */}
        <div className='mb-3'>
          <Form.Item
            name='name'
            className='mb-0'
          >
            {disableContactSearch ? (
              <Input
                placeholder={String(t('full_name'))}
                className='border border-solid border-sd-input bg-sd-input-bg h-[40px] rounded-md w-full'
                onChange={(e) => form?.setFieldsValue({ name: e.target.value })}
              />
            ) : (
              <AutoComplete
                data-testid='name-autocomplete'
                bordered={false}
                options={
                  contactsLoading
                    ? []
                    : nameOptions?.map((option) => ({
                        value: option.value,
                        label: (
                          <div className='flex justify-between items-center'>
                            <span>{option.value}</span>
                            <DeleteIcon
                              data-testid='delete-button'
                              className='cursor-pointer'
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteContact(option.uid);
                              }}
                              width={19}
                              height={21}
                              color={THEME_VARIABLE.customTunaPrimary}
                            />
                          </div>
                        ),
                      }))
                }
                placeholder={t('full_name')}
                className='border custom-select-placeholder border-solid relative border-sd-input bg-sd-input-bg h-[40px] rounded-md w-full'
                filterOption={(inputValue, option) =>
                  option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
                onChange={(value) => {
                  form?.setFieldsValue({ name: value });
                }}
                onSelect={handleNameSelect}
              >
                {contactsLoading && (
                  <Option key='loading'>
                    <Spin size='small' />
                  </Option>
                )}
              </AutoComplete>
            )}
          </Form.Item>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
          <div className='flex items-center gap-1' style={{ direction: 'ltr' }}>
            <div className='flex gap-x-2 flex-1'>
              <PhoneNumberInput
                form={form}
                countryCodeName='countryCodePrimary'
                phoneNumberName='phoneNumberPrimary'
                phoneNumberPlaceholder={t('primary_phone')}
              />
            </div>
            <span className='text-sd-destructive text-sm'>*</span>
          </div>
          <div className='flex gap-x-2' style={{ direction: 'ltr' }}>
            <PhoneNumberInput
              form={form}
              countryCodeName='countryCodeSecondary'
              phoneNumberName='secondaryPhoneNumber'
              phoneNumberPlaceholder={t('secondary_phone')}
              required={false}
            />
          </div>
        </div>
      </div>
      <div className='border-t border-sd-border my-4' />
      {/* Location section */}
      <div className='mb-4'>
        <div className='flex items-center gap-2 mb-3'>
          <MapPin className='h-3.5 w-3.5 text-sd-muted-foreground' strokeWidth={1.5} />
          <span className='text-[10px] font-semibold uppercase tracking-wider text-sd-muted-foreground'>
            {String(t('location'))}
          </span>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3'>
          <div className='flex items-center gap-1'>
            <Form.Item
              name='province'
              className='mb-0 flex-1'
              rules={[{ required: true, message: t('this_field_is_required') }]}
            >
              <Select
                loading={provinceListLoading}
                placeholder={t('city')}
                variant='borderless'
                className='border border-solid custom-select-item placeholder:capitalize custom-select-placeholder border-sd-input bg-sd-input-bg h-[40px] rounded-md'
                onChange={handleProvinceChange}
                showSearch
                filterOption={filterOptions}
                data-testid='province-select'
              >
                {provinceList?.object?.map((province) => (
                  <Option key={province.uid} value={province.province_code}>
                    {province.province}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <span className='text-sd-destructive text-sm'>*</span>
          </div>
          <div className='flex items-center gap-1'>
            <Form.Item
              name='region_uid'
              className='mb-0 flex-1'
              rules={[{ required: true, message: t('this_field_is_required') }]}
              validateTrigger='onBlur'
            >
              <Select
                loading={areaListLoading}
                placeholder={t('area')}
                variant='borderless'
                className='border border-solid custom-select-item placeholder:capitalize custom-select-placeholder bg-sd-input-bg border-sd-input h-[40px] rounded-md disabled:opacity-50'
                onChange={handleAreaChange}
                showSearch
                filterOption={filterOptions}
                disabled={!(areaList?.object && areaList.object.length > 0)}
              >
                {areaList?.object.map((area) => (
                  <Option key={area.uid} value={area.uid}>
                    {area.area_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <span className='text-sd-destructive text-sm'>*</span>
          </div>
        </div>
        <div className='flex items-center gap-1'>
          <Form.Item
            className='mb-0 flex-1'
            name='address'
            validateTrigger='onBlur'
          >
            <Input
              placeholder={t('full_address')}
              className='border border-solid border-sd-input bg-sd-input-bg h-[40px] rounded-md focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2'
              onChange={(e) => {
                form?.setFieldsValue({ address: e.target.value });
              }}
            />
          </Form.Item>
          <span className='text-sd-destructive text-sm'>*</span>
        </div>
      </div>
      {!selectedUid && !disableContactSearch && (
        <div className='flex items-center gap-2'>
          <Form.Item
            initialValue={saveAddressChecked}
            className='mb-0'
            name='saveToAddress'
            valuePropName='checked'
            noStyle
          >
            <Checkbox
              checked={saveAddressChecked}
              onChange={handleCheckboxChange}
              className='custom-checkbox-round'
            />
          </Form.Item>
          <label className='text-sm leading-5 text-sd-foreground cursor-pointer'>
            {t('save_to_address')}
          </label>
        </div>
      )}
    </Card>
  );
};

export default CustomerDetailsCard;
